import React, {useEffect, useState} from 'react';

import {useIntl} from 'react-intl';

import {colors, SearchInput, useScript} from '@stryd/react-ui';

import {Filter} from './CoachesFilter';
import {FilterButton} from './FilterButton';
import {FilterContainer} from './FilterContainer';
import {FilterMenu} from './FilterMenu';

const waitForGoogle = (callback: () => void) => {
  if ((window as any).google === undefined) {
    setTimeout(() => waitForGoogle(callback), 100);
  } else {
    callback();
  }
};

const googlePlacesScriptConfig = {
  url: `https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&libraries=places`,
  waitFor: waitForGoogle,
  async: true,
  defer: true,
};

type PlaceDetails = {
  value: string;
  label: string;
};

type PlacesAutoCompleteFilterProps = {
  filterId: string;
  filter: Filter;
  placeholder: string;
  onOutsideClick: () => void;
  onMenuButtonClick: () => void;
  onFilterChange: (event: any) => void;
  onSaveButtonClick: (event: any) => void;
  onResetButtonClick: (filterId: string) => void;
};

const PlacesAutoCompleteFilter: React.FC<
  React.PropsWithChildren<PlacesAutoCompleteFilterProps>
> = ({
  onOutsideClick,
  onMenuButtonClick,
  onFilterChange,
  onSaveButtonClick,
  onResetButtonClick,
  filterId,
  filter,
  placeholder,
}) => {
  const [placeDetails, setPlaceDetails] = useState<PlaceDetails | null>(null);
  const {scriptLoaded} = useScript(googlePlacesScriptConfig);
  const intl = useIntl();

  useEffect(() => {
    if (scriptLoaded) {
      const locationSearch = document.getElementById(
        'location-search'
      ) as HTMLInputElement;
      if (!locationSearch) {
        return;
      }
      const autocomplete = new (window as any).google.maps.places.Autocomplete(
        locationSearch
      );

      // Specify only the data fields that are needed.
      autocomplete.setFields([
        'address_components',
        'geometry',
        'icon',
        'name',
      ]);

      autocomplete.addListener('place_changed', function () {
        const place = autocomplete.getPlace();
        if (!place.geometry) {
          const event = new CustomEvent('place_changed', {
            detail: {
              data: {value: '', label: ''},
            },
          });
          setPlaceDetails(null);
          onFilterChange(event);
          onSaveButtonClick(event);
          return;
        }

        //result = Place Details obj
        //results.address_components = different address details
        //addressObj.short_name = abbreviated country name
        //addressObj.long_name = full country name
        if (place.address_components) {
          for (let i = 0; i < place.address_components.length; i += 1) {
            const addressObj = place.address_components[i];
            for (let j = 0; j < addressObj.types.length; j += 1) {
              if (addressObj.types[j] === 'country') {
                setPlaceDetails({
                  value: addressObj.short_name,
                  label: addressObj.long_name,
                });
              }
            }
          }
        }
      });
    }
  }, [onFilterChange, onSaveButtonClick, scriptLoaded]);

  useEffect(() => {
    const closeOnOutsideClick = (event: Event) => {
      const clicked = event.target as Element;

      if (!clicked.closest(`#${filterId}`)) {
        event.stopPropagation();
        onOutsideClick();
      }
    };

    if (filter.menuOpen) {
      window.addEventListener('click', closeOnOutsideClick);
    } else {
      window.removeEventListener('click', closeOnOutsideClick);
    }

    return () => {
      window.removeEventListener('click', closeOnOutsideClick);
    };
  }, [filter.menuOpen, filterId, onOutsideClick]);

  return (
    <FilterContainer id={filterId}>
      <FilterButton
        isActive={filter.active}
        menuOpen={filter.menuOpen}
        onClick={onMenuButtonClick}
        filterValue={filter.value}
        label={intl.formatMessage(filter.buttonLabelDescriptor)}
      />
      <FilterMenu
        filterId={filterId}
        menuOpen={filter.menuOpen}
        onResetClick={() => {
          onResetButtonClick(filterId);
        }}
        onSaveClick={(event: any) => {
          event.data = placeDetails;
          onFilterChange(event);
          onSaveButtonClick(event);
        }}
      >
        <SearchInput
          id="location-search"
          placeholder={placeholder}
          value={
            filter.value.length > 0 && placeDetails
              ? placeDetails.label
              : filter.value
          }
          submitButton={false}
          label="Location Search"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            onFilterChange(event);
            if (placeDetails)
              setPlaceDetails({...placeDetails, label: event.target.value});
          }}
          inputStyle={{
            backgroundColor: colors.themes.dark.backgroundInverted,
            color: colors.themes.dark.textInverted,
            borderRadius: '8px',
          }}
          onSubmit={(event) => {
            event.preventDefault();
          }}
          width="200px"
        />
      </FilterMenu>
    </FilterContainer>
  );
};

export default PlacesAutoCompleteFilter;
